import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { getMonth, isEqual, startOfMonth } from "date-fns";
import { useDateFormatter } from "../../hooks/useDateFormatter";

interface Props {
  months: Date[];
  monthForValue: Date;
  setMonth: (month: Date) => void;
}

const DoubleMonthsSlider = ({ months, monthForValue, setMonth }: Props) => {
  const formatDate = useDateFormatter();
  const monthRefArray = useRef<Map<number, HTMLDivElement> | null>(null);
  const [sliderStyles, setSliderStyles] = useState({});
  const onMonthClick = useCallback(
    (monthIndex: number) => {
      monthIndex === months.length - 1
        ? setMonth(months[monthIndex - 1])
        : setMonth(months[monthIndex]);
    },
    [months, setMonth]
  );

  const firstSelectedMonthIndex = useMemo(() => {
    return months.findIndex((v) => isEqual(v, startOfMonth(monthForValue)));
  }, [monthForValue, months]);

  const setPositionForSlider = useCallback(() => {
    if (firstSelectedMonthIndex >= 0) {
      const firstElement = getMonthsRefArray().get(0)?.getBoundingClientRect();
      const selectedElement = getMonthsRefArray()
        .get(firstSelectedMonthIndex)
        ?.getBoundingClientRect();
      const nextElement = getMonthsRefArray()
        .get(firstSelectedMonthIndex + 1)
        ?.getBoundingClientRect();
      setSliderStyles({
        left: `${selectedElement && firstElement ? selectedElement.left - firstElement.left : 0}px`,
        width: `${
          selectedElement && nextElement
            ? nextElement.width + (nextElement.left - selectedElement.left) + 20
            : 86
        }px`,
      });
    }
  }, [firstSelectedMonthIndex]);

  useEffect(() => {
    if (!!monthRefArray.current) {
      setPositionForSlider();
    }
  }, [monthRefArray, setPositionForSlider]);

  function getMonthsRefArray() {
    if (!monthRefArray.current) {
      monthRefArray.current = new Map();
    }
    return monthRefArray.current;
  }

  const getMonthWithoutDot = useCallback(
    (month) => {
      const formattedDate = formatDate(month, "monthNameShort");
      return formattedDate.endsWith(".")
        ? formattedDate.slice(0, formattedDate.length - 1)
        : formattedDate;
    },
    [formatDate]
  );

  return (
    <MainContainer>
      <SelectionMarker style={sliderStyles} />
      <MonthContainer>
        {months.map((month, index) => (
          <SingleMonth
            key={index}
            onClick={() => onMonthClick(index)}
            ref={(node) => {
              const map = getMonthsRefArray();
              if (node) {
                map.set(index, node);
              } else {
                map.delete(index);
              }
            }}
          >
            {getMonth(month) === 0 && <Year>{formatDate(month, "fullYear")}</Year>}
            <MonthName
              isActive={index === firstSelectedMonthIndex || index === firstSelectedMonthIndex + 1}
            >
              {getMonthWithoutDot(month)}
            </MonthName>
          </SingleMonth>
        ))}
      </MonthContainer>
    </MainContainer>
  );
};

const MonthName = styled.div<{ isActive: boolean }>`
  font-family: ${({ theme }) => theme.heavyFont};
  font-size: 11px;
  line-height: 11px;
  transition: color 0.4s ease-out;
  color: ${({ theme, isActive }) =>
    !!theme.form.calendar.slider
      ? isActive
        ? theme.form.calendar.slider.activeMonthsColor
        : theme.form.calendar.slider.nonActiveMonthsColor
      : isActive
      ? theme.primaryColor
      : theme.greyColor};
`;

const Year = styled.div`
  position: absolute;
  left: 50%;
  top: -20px;
  transform: translateX(-50%);
  font-size: 9px;
  font-family: ${({ theme }) => theme.regularFont};
  line-height: 11px;
  color: ${({ theme }) => theme.greyColor};
`;

const SingleMonth = styled.div`
  cursor: pointer;
  position: relative;
`;

const MainContainer = styled.div`
  position: relative;
  width: 100%:
  height: 100%:
`;

const SelectionMarker = styled.div`
  height: 100%;
  position: absolute;
  transition: left 0.4s ease-out, width 0.4s ease-out;
  top: 0;
  border-radius: 11px;
  background-color: ${({ theme }) =>
    !!theme.form.calendar.slider
      ? theme.form.calendar.slider.activeMonthsBackground
      : theme.primaryColor};
`;

const MonthContainer = styled.div`
  position: relative;
  display: flex;
  padding: 0 10px;
  font-family: ${({ theme }) => theme.regularFont};
  margin-bottom: 20px;
  justify-content: space-between;
  height: 22px;
  align-items: center;
`;

export default DoubleMonthsSlider;
